import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../app/rootReducer';

export interface CoveredDeviceConfig {
  deviceAsin: string;
  deviceType: string;
  deviceConfigUid: number;
  version: string;
}

export interface ExtendedWarrantyConfiguration {
  marketplaceId: string;
  warrantyAsin: string;
  warrantyLengthYear: number;
  warrantyLengthMonth: number | null;
  warrantyLengthDay: number | null;
  warrantyGracePeriodDay: number;
  coveredDeviceConfigs?: CoveredDeviceConfig[];
  numberOfComponentsCoveredByWarrantyConfiguration?: number;
  minimumDayForPostClaimWarrantyExtension?: number;
  purchaseEligibilityDay?: number;
  deviceType?: string;
  canonicalMarketplaceId?: string;
  version?: string;
}

export function instanceOfExtendedWarrantyConfiguration(object: any): object is ExtendedWarrantyConfiguration {
    // Check if the object has coveredDeviceConfigs property - key identifier for ExtendedWarrantyConfiguration
    return object && ('coveredDeviceConfigs' in object || 'warrantyAsin' in object);
}

export interface LimitedWarrantyConfiguration {
  marketplaceId: string;
  configurationDeviceAsin: string;
  warrantyLengthYear: number;
  warrantyLengthMonth: number | null;
  warrantyLengthDay: number | null;
  warrantyGracePeriodDay: number;
  worryFree: boolean;
  minimumDayForPostClaimWarrantyExtension: number;
  deviceType?: string;
  canonicalMarketplaceId?: string;
  version?: string;
}

export interface Configuration {
  data: ExtendedWarrantyConfiguration | LimitedWarrantyConfiguration;
}

export interface UploadStatus {
  isUploading: boolean;
  progress: number;
  errors?: string[];
}

interface ConfigurationState {
  configurations: Configuration[];
  uploadStatus: UploadStatus;
  isLoading: boolean;
  errors?: string[];
}

const initialState: ConfigurationState = {
  configurations: [],
  uploadStatus: {
    isUploading: false,
    progress: 0
  },
  isLoading: false
};

export const configurationSlice = createSlice({
  name: 'configuration',
  initialState,
  reducers: {
    // Upload actions
    uploadConfigurationStart(state: ConfigurationState) {
      state.uploadStatus.isUploading = true;
      state.uploadStatus.progress = 0;
      state.uploadStatus.errors = undefined;
    },
    uploadConfigurationProgress(state: ConfigurationState, action: PayloadAction<number>) {
      state.uploadStatus.progress = action.payload;
    },
    uploadConfigurationSuccess(state: ConfigurationState) {
      state.uploadStatus.isUploading = false;
      state.uploadStatus.progress = 100;
      state.uploadStatus.errors = undefined;
    },
    uploadConfigurationFailure(state: ConfigurationState, action: PayloadAction<string | string[]>) {
      state.uploadStatus.isUploading = false;
      if (Array.isArray(action.payload)) {
        state.uploadStatus.errors = action.payload;
      } else {
        state.uploadStatus.errors = [action.payload];
      }
    },

    // Fetch actions
    fetchConfigurationsStart(state: ConfigurationState) {
      state.isLoading = true;
      state.errors = undefined;
    },
    fetchConfigurationsSuccess(state: ConfigurationState, action: PayloadAction<Configuration[]>) {
      state.isLoading = false;
      state.configurations = action.payload;
      state.errors = undefined;
    },
    fetchConfigurationsFailure(state: ConfigurationState, action: PayloadAction<string | string[]>) {
      state.isLoading = false;
      if (Array.isArray(action.payload)) {
        state.errors = action.payload;
      } else {
        state.errors = [action.payload];
      }
    },

    // Clear actions
    clearUploadStatus(state: ConfigurationState) {
      state.uploadStatus = {
        isUploading: false,
        progress: 0,
        errors: undefined
      };
    },
    clearConfigurations(state: ConfigurationState) {
      state.configurations = [];
    }
  }
});

// Export actions
export const {
  uploadConfigurationStart,
  uploadConfigurationProgress,
  uploadConfigurationSuccess,
  uploadConfigurationFailure,
  fetchConfigurationsStart,
  fetchConfigurationsSuccess,
  fetchConfigurationsFailure,
  clearUploadStatus,
} = configurationSlice.actions;

// Export selectors
export const selectConfigurations = (state: RootState) => state.configuration.configurations;
export const selectUploadStatus = (state: RootState) => state.configuration.uploadStatus;
export const selectIsLoading = (state: RootState) => state.configuration.isLoading;
// No longer needed as we're only using errors array
export const selectErrors = (state: RootState) => state.configuration.errors;

export default configurationSlice.reducer;
